import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Col, Row, message } from 'antd';
import { UnifiedIDE } from '../IDE/ide';
import { getIDEConfigDefault, LanguageId } from '../IDE/ide/core/types';
import { IDEConsoleStudentProps } from './types';
import { validateCodeWithRules } from './utils/validation';
import { ConsoleOutput } from '../Console/types';
import { useUserApi } from '../../api/user/userApp';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { TUserAnswer } from '../../models/user/UserProgression';
import Console from '../Console';
import { IDEConsoleBridge } from './utils/executionBridge';
import ResponsiveConsoleIDELayout from './ResponsiveConsoleIDELayout';
import './styles.css';

const IDEConsoleStudent: React.FC<IDEConsoleStudentProps> = ({ 
  lesson, 
  language,
  checkResult, 
  onComplete,
  onCodeChange,
}) => {
  const [currentCode, setCurrentCode] = useState('');
  const [consoleOutput, setConsoleOutput] = useState<ConsoleOutput[]>([]);
  const [isWin, setIsWin] = useState(false);
  // 👉 Track current code like IDEPhaserStudent (following IDEPhaser pattern)
  const [latestCode, setLatestCode] = useState<string>('');
  const [answer, setAnswer] = useState<TUserAnswer | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initialCodeRef = useRef<string>('');
  
  const { getUserAnswerById, saveUserAnswerById } = useUserApi();
  const { getUserAppId } = useTilmiAuth();

  // Create execution bridge for proper console output capture
  const executionBridge = useMemo(() => {
    return new IDEConsoleBridge((outputs) => {
      setConsoleOutput(outputs);
    });
  }, []);

  // Clear console when starting new execution
  const handleRunStart = () => {
    executionBridge.clear();
  };

  // Initialize with lesson data and load user answer (following IDEPhaserStudent pattern)
  useEffect(() => {
    if (!lesson) {
      return;
    }

    // Prevent re-initialization if already initialized
    if (isInitialized) {
      return;
    }

    getUserAnswerById(lesson.id)
      .then(resp => {
        const userAnswer = resp.data;
        setAnswer(userAnswer);
        
        // Get lesson config
        const lessonConfig = lesson.config?.config as any;
        let config = lessonConfig;
        if (!config) {
          config = {
            configMirror: getIDEConfigDefault(),
          };
        }
        
        // Ensure configMirror exists
        if (!config.configMirror) {
          config.configMirror = getIDEConfigDefault();
        }
        
        // Use user's saved code if available, otherwise use lesson config
        if (userAnswer?.workspace) {
          config.configMirror.initialCode = userAnswer.workspace;
        }
        
        const initialCode = config.configMirror?.initialCode || config.initialCode || '';
        initialCodeRef.current = initialCode; // Store initial code in ref
        setLatestCode(initialCode);
        setCurrentCode(initialCode);
        setIsInitialized(true);
      })
      .catch(err => {
        // If no answer exists, use lesson config
        const lessonConfig = lesson.config?.config as any;
        let config = lessonConfig;
        if (!config) {
          config = {
            configMirror: getIDEConfigDefault(),
          };
        }
        
        // Ensure configMirror exists
        if (!config.configMirror) {
          config.configMirror = getIDEConfigDefault();
        }
        
        const initialCode = config.configMirror?.initialCode || config.initialCode || '';
        initialCodeRef.current = initialCode; // Store initial code in ref
        setLatestCode(initialCode);
        setCurrentCode(initialCode);
        setIsInitialized(true);
      });
  }, [lesson?.id, isInitialized]);

  // Reset initialization when lesson changes
  useEffect(() => {
    if (lesson?.id) {
      setIsInitialized(false);
    }
  }, [lesson?.id]);

  const handleCodeChange = (code: string) => {
    setCurrentCode(code);
    // 👉 Update latestCode for persistence (following IDEPhaserStudent pattern)
    setLatestCode(code);
    onCodeChange?.(code);
  };

  // Win condition check (following IDEPhaser pattern)
  useEffect(() => {
    if (isWin) checkResult?.(true);
  }, [isWin, checkResult]);

  const onWin = () => setIsWin(true);

  // Handle console log from UnifiedIDE
  const handleConsoleLog = (message: string) => {
    const newOutput: ConsoleOutput = {
      id: Date.now().toString(),
      type: 'log',
      message,
      timestamp: new Date(),
    };
    setConsoleOutput(prev => [...prev, newOutput]);
  };

  // Handle code execution stop and validation (following IDEPhaser pattern)
  const handleStop = async () => {
    // Save the current code first (following IDEPhaserStudent pattern)
    await handleRunAndSave(latestCode);
    
    // Get lesson config and validation rules
    const lessonConfig = lesson?.config?.config as any;
    
    // Try different paths for validation rules
    let validationRules = [];
    if (lessonConfig?.validationRules) {
      validationRules = lessonConfig.validationRules;
    } else if (lesson?.config?.validationRules) {
      validationRules = lesson.config.validationRules;
    } else if (lessonConfig?.config?.validationRules) {
      validationRules = lessonConfig.config.validationRules;
    } else if (lesson?.validationRules) {
      validationRules = lesson.validationRules;
    }
    
    // If no validation rules found, create a default one for print function
    if (validationRules.length === 0 && latestCode.includes('print(')) {
      validationRules = [{
        id: 'python_print_required',
        type: 'pattern',
        condition: 'require_print',
        message: 'You must use the print() function to display output.',
        language: 'python',
        severity: 'error',
        required: true
      }];
    }
    
    // Test the require_print validator directly
    if (validationRules.length > 0 && validationRules[0].condition === 'require_print') {
      const { pythonValidators } = await import('./utils/validation');
      const testResult = pythonValidators.require_print(latestCode);
    }
    
    // Use new validation system
    const result = validateCodeWithRules(
      latestCode,
      validationRules,
      language,
      consoleOutput.map(output => output.message || '')
    );
    
    // Add basic checks if no validation rules are defined
    if (validationRules.length === 0) {
      const hasCode = latestCode.trim().length > 0;
      const hasOutput = consoleOutput.length > 0;
      
      if (!hasCode) {
        result.errors.push('Please write some code before submitting.');
      }
      
      if (!hasOutput) {
        result.errors.push('Please run your code to see output before submitting.');
      }
      
      result.isValid = result.errors.length === 0;
    }

                 // Validation result is handled by message.success/error below

                 // Standard win condition flow (following IDEPhaser pattern)
             if (result.isValid) {
               // La reussite est annoncee par la vue parente (LessonContent),
               // seule et traduite : un message ici s'empilerait avec le sien.
               onWin();
             } else {
               // Show specific validation error messages
               if (result.errors.length > 0) {
                 result.errors.forEach(error => {
                   message.error(error, 5);
                 });
               } else {
                 message.error('Essayez encore! Vérifiez que vous avez bien respecté toutes les conditions.', 3);
               }
               checkResult?.(false);
             }
  };

  const handleRunAndSave = async (userCode: string) => {
    if (!lesson) return;

    const lessonAnswer: TUserAnswer = {
      id: answer?.id ?? 0,
      userapp_id: getUserAppId(),
      lesson_id: lesson.id,
      workspace: userCode,
    };

    saveUserAnswerById(lesson.id, lessonAnswer)
      .then((resp) => {
        setAnswer(resp.data);
      })
      .catch((err) => {
        message.error(err.response?.data ?? err.message ?? err);
      });
  };

  // Don't render if lesson is not available or not initialized
  if (!lesson || !isInitialized) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading lesson...</div>
      </div>
    );
  }

  const ideComponent = (
    <div className="ideconsole-hide-help" style={{ height: '100%' }}>
      <UnifiedIDE
        key={`ide-${lesson?.id}-${isInitialized}`}
        language={language}
        initialCode={initialCodeRef.current}
        projectMode={false}
        consoleMode={true}
        adminMode={false}
        sprites={[]}
        scenesConfig={[]}
        obstacles={[]}
        sounds={[]}
        helpConfig={{}}
        helpGrouped={false}
        bridge={executionBridge}
        onStop={handleStop}
        onRestoreInitial={() => {
          const initialCode = initialCodeRef.current;
          setLatestCode(initialCode);
          setCurrentCode(initialCode);
        }}
        onCodeChange={handleCodeChange}
        onLog={handleConsoleLog}
      />
    </div>
  );

  const consoleComponent = (
    <Console
      output={consoleOutput}
      height={400}
      theme="dark"
      onClear={() => {
        executionBridge.clear();
      }}
    />
  );

  return (
    <ResponsiveConsoleIDELayout
      ideComponent={ideComponent}
      consoleComponent={consoleComponent}
      style={{ width: '100%', height: '100%' }}
    />
  );
};

export default IDEConsoleStudent;
